import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';

/**
 * Ensures a navigation is performed by an authenticated user.
 *
 * On a cold start (no user in memory) it hydrates the session from storage —
 * which also re-syncs the active role into `PermissionsService` — and, when no
 * user can be resolved, redirects to the configured login page preserving the
 * intended destination as `returnUrl`.
 *
 * Authorization (which role/permission may enter a given route) is intentionally
 * NOT handled here: that responsibility lives in the permission guard.
 *
 * @param auth - Auth service holding/restoring the current user.
 * @param router - Router used to build the login redirect.
 * @param returnUrl - URL the user was trying to reach, kept for post-login redirect.
 * @returns `true` when authenticated, or a `UrlTree` redirecting to login.
 */
async function resolveAuth(auth: AuthService, router: Router, returnUrl: string): Promise<boolean | UrlTree> {
  if (auth.user === null) {
    await auth.loadStorageAuth();
  }

  if (auth.user) return true;

  return router.createUrlTree(['/' + environment.CUSTOMIZATION.PATHLOGIN], { queryParams: { returnUrl } });
}

/** Builds the URL string for a `canMatch` navigation from its consumed segments. */
function segmentsToUrl(segments: UrlSegment[]): string {
  return '/' + segments.map((segment) => segment.path).join('/');
}

/** Functional `canActivate` guard enforcing authentication. */
export const authGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => resolveAuth(inject(AuthService), inject(Router), state.url);

/**
 * Functional `canMatch` guard enforcing authentication. It both blocks the match
 * and prevents the lazy chunk from loading for unauthenticated users.
 */
export const authMatchGuard: CanMatchFn = (_route, segments: UrlSegment[]) => resolveAuth(inject(AuthService), inject(Router), segmentsToUrl(segments));
